
import { useState, useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import BenefitsBar from './components/BenefitsBar';
import ProductGrid from './components/ProductGrid';
import AboutUs from './components/AboutUs';
import Footer from './components/Footer';
import Cart from './components/Cart';
import ProductModal from './components/ProductModal';
import OrderForm from './components/OrderForm';

interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  description: string;
  category: string;
  isNew?: boolean;
  isOnSale?: boolean;
}

export default function HomePage() {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);

  const handleAddToCart = (productId: string, quantity: number, productName: string, productPrice: number, productImage: string) => {
    setCartItems(prev => {
      const existingItem = prev.find(item => item.id === productId);
      if (existingItem) {
        return prev.map(item =>
          item.id === productId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prev, {
          id: productId,
          name: productName,
          price: productPrice,
          image: productImage,
          quantity
        }];
      }
    });
  };

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setIsProductModalOpen(true);
  };

  const handleViewDetails = (product: Product) => {
    setSelectedProduct(product);
    setIsProductModalOpen(true);
  };

  const handleCloseProductModal = () => {
    setIsProductModalOpen(false);
    setSelectedProduct(null);
  };

  const handleUpdateQuantity = (id: string, quantity: number) => {
    setCartItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  const handleRemoveItem = (id: string) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  const handleClearCart = () => {
    setCartItems([]);
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    setShowOrderForm(true);
  };

  const handleOrderComplete = () => {
    setShowOrderForm(false);
    setCartItems([]);
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-white">
      <Header cartCount={cartItems.length} onCartClick={() => setIsCartOpen(true)} />
      <Hero />
      <BenefitsBar />
      <ProductGrid 
        onAddToCart={handleAddToCart}
      />
      <AboutUs />
      <Footer />

      {/* Modals */}
      {isCartOpen && (
        <Cart 
          items={cartItems}
          onClose={() => setIsCartOpen(false)}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onCheckout={handleCheckout}
        />
      )}

      {selectedProduct && (
        <ProductModal 
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
        />
      )}

      {showOrderForm && (
        <OrderForm 
          cartItems={cartItems}
          onClose={() => setShowOrderForm(false)}
          onOrderComplete={handleOrderComplete}
        />
      )}
    </div>
  );
}
